// Typed wrappers for the agents surface (decision D20, slice L1b-ii): registry, catalog sync, learnings,
// scorecards and recommendations. The API enforces every permission; the UI only avoids dead ends.
import type {
  AgentLearning,
  AgentProfileVersion,
  AgentRecommendation,
  AgentRegistryEntry,
  AgentScorecard,
  CreateLearningInput,
  DecideRecommendationInput,
  LearningKind,
  LearningStatus,
  MemoryScope,
  ReviewLearningInput,
} from "@maestros/contracts";
import { api } from "./client.ts";

const enc = encodeURIComponent;
const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});

export type SyncAction = "created" | "updated" | "unchanged" | "update_available" | "key_conflict";

export interface CatalogSyncResult {
  catalog: { name: string; version: string };
  agents: Record<SyncAction, number> & { items: { key: string; id: string; version: string; action: SyncAction }[] };
  learnings: { imported: number; unchanged: number; superseded: number; skipped: number; packs: { pack: string; imported: number; unchanged: number; superseded: number; skipped: number }[] };
}

export interface AgentDetail {
  agent: AgentRegistryEntry;
  versions: AgentProfileVersion[];
}

export interface ScorecardItem extends AgentScorecard {
  version_number: number | null;
  min_sample: number;
}

export interface ScorecardsResponse {
  window_days: number;
  computed_at: string;
  items: ScorecardItem[];
}

export interface RecommendationsResponse {
  window_days: number;
  computed_at: string;
  items: AgentRecommendation[];
}

export type LearningListItem = AgentLearning & { rank: number | null };

/** Redacted independent reviews returned by GET /learning-reviews, tenant scoped and bounded. */
export interface IndependentLearningReview {
  id: string;
  learning_id: string;
  learning_version: number;
  reviewer_profile_id: string;
  reviewer_version_id: string | null;
  model_call_id: string | null;
  decision: Extract<ReviewLearningInput["decision"], "approve" | "reject"> | "defer";
  reason: string;
  evidence_refs: string[];
  evidence_fingerprint: string;
  result: Record<string, unknown>;
  created_by_actor?: AgentProfileVersion["created_by_actor"];
  created_at: string;
}

export interface LearningFilters {
  status?: LearningStatus;
  scope?: MemoryScope;
  kind?: LearningKind;
  q?: string;
  limit?: number;
}

/** Every member reads the registry (project agents only for projects they can see). */
export const listAgents = (signal?: AbortSignal) => api<{ items: AgentRegistryEntry[] }>("/agents", withSignal(signal));

export const getAgent = (id: string, signal?: AbortSignal) => api<AgentDetail>(`/agents/${enc(id)}`, withSignal(signal));

/** Organization admin session only. Idempotent: a second call reports everything unchanged. */
export const syncAgentCatalog = () => api<CatalogSyncResult>("/agents/catalog/sync", { method: "POST" });

export const listLearnings = (filters: LearningFilters, signal?: AbortSignal) =>
  api<{ items: LearningListItem[] }>("/agent-learnings", { query: { ...filters }, ...withSignal(signal) });

/** Maintainers/admins only. Fetch the latest review for each visible learning, never a global history. */
export const listLearningReviews = (learningIds: readonly string[], signal?: AbortSignal) => {
  if (learningIds.length === 0) return Promise.resolve({ items: [] as IndependentLearningReview[] });
  return api<{ items: IndependentLearningReview[] }>("/learning-reviews", {
    query: { learning_ids: learningIds.join(","), limit: learningIds.length }, ...withSignal(signal),
  });
};

/** Maintainers and admins; answers 422 when the text carries personal data or a credential. */
export const createLearning = (body: CreateLearningInput) => api<AgentLearning & { duplicate: boolean }>("/agent-learnings", { method: "POST", body });

/** Organization admin/maintainer session; `expected_version` guards against concurrent reviews (409). */
export const reviewLearning = (id: string, body: ReviewLearningInput) => api<AgentLearning>(`/agent-learnings/${enc(id)}/review`, { method: "POST", body });

export const getScorecards = (windowDays: number, signal?: AbortSignal) => api<ScorecardsResponse>("/agents/scorecards", { query: { window_days: windowDays }, ...withSignal(signal) });

export const getRecommendations = (windowDays: number, signal?: AbortSignal) =>
  api<RecommendationsResponse>("/agents/recommendations", { query: { window_days: windowDays }, ...withSignal(signal) });

/** Organization admin/maintainer session; once per recommendation (409 when already decided). */
export const decideRecommendation = (body: DecideRecommendationInput) => api<AgentRecommendation>("/agents/recommendations/decide", { method: "POST", body });
